import { Button, Flex, Space, Table } from 'antd'
import { FC, useMemo, useState } from 'react'
import PageLayout from '../../components/page-layout/page-layout'
import useEndpointQueryParams from '../../hooks/table-pages/use-endpoint-query-parameters'
import useTablePagination from '../../hooks/table-pages/use-table-pagination'
import useFiltersForm from '../../hooks/forms/use-filters-form'
import useErrorModal from '../../hooks/api/use-error-modal'
import { ordersApi, useGetMyOrdersQuery } from '../../store/api/orders-api'
import { useAppDispatch } from '../../store/store'
import Order from '../../types/api/order'
import FilterForm from './components/filters-form/filters-form'
import HelpDrawer from './components/help-drawer/help-drawer'
import OrderDrawer from './components/order-drawer/order-drawer'
import { columns } from './constants/columns'
import { defaultParams } from './constants/default-params'
import { deserializeFilters } from './helpers/deserialize-filters'

const OrdersPage: FC = () => {
    const dispatch = useAppDispatch()
    const params = useEndpointQueryParams({ deserializeFilters })
    const queryArg = useMemo(() => ({ ...params, ...defaultParams }), [params])
    const { data, isError, error, isFetching } = useGetMyOrdersQuery(queryArg)
    const pagination = useTablePagination(queryArg, data)
    const { form: filterForm, onSearch, onClear } = useFiltersForm()

    useErrorModal({ isError, error }, 'Ошибка загрузки заказов')

    const [drawerOpen, setDrawerOpen] = useState(false)
    const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create')
    const [selectedDocId, setSelectedDocId] = useState<string | undefined>()

    const openCreate = () => {
        setDrawerMode('create')
        setSelectedDocId(undefined)
        setDrawerOpen(true)
    }

    const openEdit = (order: Order) => {
        setDrawerMode('edit')
        setSelectedDocId(order.documentId)
        setDrawerOpen(true)
    }

    const closeDrawer = () => {
        setDrawerOpen(false)
        setSelectedDocId(undefined)
    }

    const handleSaved = () => {
        dispatch(ordersApi.util.invalidateTags(['Orders']))
    }

    return (
        <PageLayout title='Заказы' beforeUser={<HelpDrawer />}>
            <Flex gap={24} align='center' justify='space-between'>
                <FilterForm form={filterForm} onSearch={onSearch} onClear={onClear} />
                <Space>
                    <Button type='primary' onClick={openCreate}>
                        Создать заказ
                    </Button>
                </Space>
            </Flex>
            <Table
                size='small'
                rowKey={row => row.documentId}
                columns={columns}
                dataSource={data?.data ?? []}
                loading={isFetching}
                pagination={pagination}
                onRow={record => ({
                    onClick: () => openEdit(record),
                    style: { cursor: 'pointer' },
                })}
                locale={{ emptyText: 'Нет заказов' }}
            />
            <OrderDrawer
                open={drawerOpen}
                mode={drawerMode}
                orderDocumentId={selectedDocId}
                onClose={closeDrawer}
                onSaved={handleSaved}
            />
        </PageLayout>
    )
}

export default OrdersPage
